import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from './firebase';
import { registerUser } from './api';
import { useAuth } from './contexts/AuthContext';
import { getJobBranding } from './services/jobService';
import useDocumentTitle, { useHideFavicon } from './hooks/useDocumentTitle';

// Login header. branding: undefined = still loading a job link's branding
// (header kept invisible but sized, so neither Vagmee nor an empty gap
// flashes first); null / no name+logo = the regular Vagmee header, exactly
// as before; otherwise "<Company> powered by Vagmee" for that job's link.
function LoginBrandHeader({ branding }) {
  const [brokenLogo, setBrokenLogo] = useState('');
  const name = (branding?.name || '').trim();
  const logo = branding?.logo && branding.logo !== brokenLogo ? branding.logo : '';

  if (name || logo) {
    return (
      <div className="login-header">
        {logo && (
          <div className="cobrand-login-logo-wrap">
            <img src={logo} alt={name || 'Company logo'} className="cobrand-login-logo" onError={() => setBrokenLogo(logo)} />
          </div>
        )}
        {name && <h1 className="login-title">{name}</h1>}
        <div className="cobrand-powered cobrand-powered--center">
          <span>powered by</span>
          <img src="/Vagmee_Logo.png" alt="" className="cobrand-vagmee-logo" />
          <strong>Vagmee</strong>
          <span className="cobrand-sep">·</span>
          <a href="https://www.vagmee.in" target="_blank" rel="noopener noreferrer">www.vagmee.in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-header" style={branding === undefined ? { visibility: 'hidden' } : undefined}>
      <div className="logo-container">
        <img src="/Vagmee_Logo.png" alt="Vagmee Logo" className="login-logo" />
      </div>
      <h1 className="login-title">Vagmee</h1>
      <p className="login-subtitle">Practice smarter. Get hired faster.</p>
    </div>
  );
}

export default function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleLogin } = useAuth();

  // ── Auth step state ────────────────────────────────────────────────────────
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Profile step state (step 2 for new users) ─────────────────────────────
  const [phase, setPhase] = useState('auth'); // 'auth' | 'profile' | 'reset'
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingProvider, setPendingProvider] = useState('email');
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');

  // ── Forgot-password step state ────────────────────────────────────────────
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const returnTo = location.state?.returnTo || searchParams.get('returnTo');
  const inviteReturnTo =
    typeof returnTo === 'string' &&
    (returnTo.startsWith('/interview/') || returnTo.startsWith('/inst/') || returnTo === '/setup')
      ? returnTo
      : null;

  // Co-branding only for a company job link (/interview/:jobId); every other
  // way into the login page keeps the plain Vagmee header.
  const brandJobId = inviteReturnTo?.startsWith('/interview/')
    ? inviteReturnTo.slice('/interview/'.length).split(/[/?#]/)[0]
    : '';
  const [branding, setBranding] = useState(brandJobId ? undefined : null);
  useEffect(() => {
    if (!brandJobId) return undefined;
    let active = true;
    getJobBranding(brandJobId).then((b) => { if (active) setBranding(b); });
    return () => { active = false; };
  }, [brandJobId]);
  useDocumentTitle(branding?.name?.trim());
  useHideFavicon(!!(branding?.name?.trim() || branding?.logo));

  // ── Shared: complete registration after profile form ──────────────────────
  const completeRegistration = async (user, provider, profile) => {
    const backendResult = await registerUser(user, provider, profile);
    if (!backendResult) {
      setError('Registration failed. Please try again.');
      setLoading(false);
      return;
    }
    const redirectTo = backendResult?.redirect_to;
    if (!redirectTo) {
      setError('Authentication incomplete. Please try logging in again.');
      setLoading(false);
      return;
    }
    handleLogin({
      user,
      redirectTo,
      entitlementStatus: backendResult?.entitlement_status,
      role: backendResult?.role,
      returnTo: inviteReturnTo,
    });
  };

  // ── Step 1: Email / password ───────────────────────────────────────────────
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        const name = profileName.trim();
        if (!name) { setError('Full name is required.'); setLoading(false); return; }
        const digits = profilePhone.replace(/\D/g, '');
        if (digits.length !== 10) { setError('Enter a valid 10-digit mobile number.'); setLoading(false); return; }

        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await completeRegistration(cred.user, 'email', {
          display_name: name,
          phone_number: digits,
        });
      } else {
        // Sign in — no profile step needed for returning users
        const cred = await signInWithEmailAndPassword(auth, email, password);
        await completeRegistration(cred.user, 'email', {});
      }
    } catch (err) {
      switch (err.code) {
        case 'auth/email-already-in-use': setError('Email already in use. Please sign in.'); break;
        case 'auth/invalid-email':        setError('Invalid email address'); break;
        case 'auth/weak-password':        setError('Password is too weak'); break;
        case 'auth/user-not-found':       setError('No account found with this email'); break;
        case 'auth/wrong-password':
        case 'auth/invalid-credential':   setError('Invalid email or password'); break;
        default:                          setError('Authentication failed. Please try again.');
      }
      setLoading(false);
    }
  };

  // ── Step 1: Google ────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const isNew = result.additionalUserInfo?.isNewUser ?? false;

      if (isNew) {
        // First-time Google user — collect phone (name pre-filled from Google)
        setPendingUser(result.user);
        setPendingProvider('google');
        setProfileName(result.user.displayName || '');
        setProfilePhone('');
        setPhase('profile');
        setLoading(false);
      } else {
        // Returning Google user — proceed immediately
        await completeRegistration(result.user, 'google', {});
      }
    } catch (err) {
      if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups and try again.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again.');
      } else {
        setError(err.message || 'Google sign-in failed.');
      }
      setLoading(false);
    }
  };

  // ── Forgot password: Firebase sends its own reset email + hosted reset
  //    page, so this needs no backend route and no Resend integration —
  //    just the client SDK call already available since Firebase Auth is
  //    already this app's sign-in provider. ─────────────────────────────────
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetSent(true);
    } catch (err) {
      switch (err.code) {
        case 'auth/invalid-email':  setError('Invalid email address'); break;
        case 'auth/user-not-found':
          // Deliberately the same success state as a real account — do not
          // reveal whether an email is registered (standard reset-flow
          // practice, avoids leaking which emails have accounts).
          setResetSent(true);
          break;
        default: setError(err.message || 'Unable to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const backToSignIn = () => {
    setPhase('auth');
    setResetEmail('');
    setResetSent(false);
    setError('');
  };

  // ── Step 2: Profile submission ────────────────────────────────────────────
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const name = profileName.trim();
    if (!name) { setError('Full name is required.'); return; }

    const digits = profilePhone.replace(/\D/g, '');
    if (digits.length !== 10) { setError('Enter a valid 10-digit mobile number.'); return; }

    setLoading(true);
    try {
      await completeRegistration(pendingUser, pendingProvider, {
        display_name: name,
        phone_number: digits,
      });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // ── Shared backgrounds ────────────────────────────────────────────────────
  const Blobs = () => (
    <div className="app-bg-blobs" aria-hidden="true">
      <div className="app-blob app-blob-1" />
      <div className="app-blob app-blob-2" />
      <div className="app-blob app-blob-3" />
    </div>
  );

  // ── Forgot-password step render ───────────────────────────────────────────
  if (phase === 'reset') {
    return (
      <div className="login-container">
        <Blobs />

        <button onClick={backToSignIn} className="back-button" title="Back to sign in">
          ← Back
        </button>

        <div className="login-card">
          <LoginBrandHeader branding={branding} />

          <div className="login-form-container">
            <h2 className="login-form-title">Reset Password</h2>

            {error && <div className="login-error">{error}</div>}

            {resetSent ? (
              <>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', lineHeight: 1.5, margin: '0 0 1.25rem' }}>
                  If an account exists for <strong>{resetEmail.trim()}</strong>, a password reset link has been sent.
                  Check your inbox (and spam folder), then follow the link to set a new password.
                </p>
                <button
                  type="button"
                  className="login-button login-button-primary"
                  onClick={backToSignIn}
                >
                  Back to Sign In
                </button>
              </>
            ) : (
              <form onSubmit={handlePasswordReset} className="login-form">
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: 1.5, margin: '0 0 0.5rem' }}>
                  Enter the email you signed up with — we'll send you a link to reset your password.
                </p>
                <div className="login-input-group">
                  <label htmlFor="resetEmail">Email</label>
                  <input
                    id="resetEmail"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="login-button login-button-primary"
                  disabled={loading || !resetEmail.trim()}
                >
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
            )}

            {!resetSent && (
              <div className="login-toggle">
                <button
                  type="button"
                  onClick={backToSignIn}
                  className="login-toggle-link"
                  disabled={loading}
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Profile step render ───────────────────────────────────────────────────
  if (phase === 'profile') {
    return (
      <div className="login-container">
        <Blobs />
        <div className="login-card">
          <LoginBrandHeader branding={branding} />

          <div className="login-form-container">
            <div className="login-step-badge">Step 2 of 2</div>
            <h2 className="login-form-title">Complete Your Profile</h2>

            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleProfileSubmit} className="login-form">
              <div className="login-input-group">
                <label htmlFor="profileName">Full Name</label>
                <input
                  id="profileName"
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Your full name"
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="login-input-group">
                <label htmlFor="profilePhone">Mobile Number</label>
                <div className="login-phone-wrap">
                  <span className="login-phone-prefix">+91</span>
                  <input
                    id="profilePhone"
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setProfilePhone(val);
                    }}
                    placeholder="10-digit mobile number"
                    required
                    disabled={loading}
                    inputMode="numeric"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-button login-button-primary"
                disabled={loading}
              >
                {loading ? 'Setting up your account…' : 'Continue →'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Auth step render (step 1) ─────────────────────────────────────────────
  return (
    <div className="login-container">
      <Blobs />

      <button onClick={() => navigate('/')} className="back-button" title="Back to Landing Page">
        ← Back
      </button>

      <div className="login-card">
        <LoginBrandHeader branding={branding} />

        <div className="login-form-container">
          <h2 className="login-form-title">{isSignUp ? 'Create Account' : 'Welcome'}</h2>

          {error && <div className="login-error">{error}</div>}

          <form onSubmit={handleEmailAuth} className="login-form">
            <div className="login-input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
              />
            </div>

            <div className="login-input-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>

            {!isSignUp && (
              <div style={{ textAlign: 'right', marginTop: '-0.5rem' }}>
                <button
                  type="button"
                  onClick={() => { setPhase('reset'); setResetEmail(email); setError(''); }}
                  className="login-toggle-link"
                  disabled={loading}
                  style={{ fontSize: '0.85rem' }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {isSignUp && (
              <>
                <div className="login-input-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>

                <div className="login-input-group">
                  <label htmlFor="signupName">Full Name</label>
                  <input
                    id="signupName"
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Your full name"
                    required
                    disabled={loading}
                    autoComplete="name"
                  />
                </div>

                <div className="login-input-group">
                  <label htmlFor="signupPhone">Mobile Number</label>
                  <div className="login-phone-wrap">
                    <span className="login-phone-prefix">+91</span>
                    <input
                      id="signupPhone"
                      type="tel"
                      value={profilePhone}
                      onChange={(e) => setProfilePhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile number"
                      required
                      disabled={loading}
                      inputMode="numeric"
                      autoComplete="tel"
                    />
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              className="login-button login-button-primary"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </button>
          </form>

          <div className="login-divider">
            <span>OR</span>
          </div>

          <button
            onClick={handleGoogleSignIn}
            className="login-button login-button-google"
            disabled={loading}
          >
            <svg className="google-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="login-toggle">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button
              type="button"
              onClick={() => { setIsSignUp(!isSignUp); setError(''); setPassword(''); setConfirmPassword(''); setProfileName(''); setProfilePhone(''); }}
              className="login-toggle-link"
              disabled={loading}
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
